import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuizIcon from '@mui/icons-material/Quiz';
import ShortTextIcon from '@mui/icons-material/ShortText';
import NotesIcon from '@mui/icons-material/Notes';
import { ApplicationMetrics } from '../../../../types/Applications';
import ApplicationService from '../../../../services/ApplicationService';

interface ApplicationOverviewPanelProps {
  organizationId: string;
  campaignId: string;
}

const ApplicationOverviewPanel: React.FC<ApplicationOverviewPanelProps> = ({ organizationId, campaignId }) => {
  const [metrics, setMetrics] = useState<ApplicationMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<boolean>(true);

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    const fetchApplicationMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const applicationService = new ApplicationService();
        const metricsData = await applicationService.getApplicationMetrics(organizationId, campaignId);
        console.log('Application metrics:', metricsData);
        setMetrics(metricsData);
      } catch (err) {
        console.error('Error fetching application metrics:', err);
        setError('Unable to load application metrics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplicationMetrics();
  }, [organizationId, campaignId]);

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="overview-content"
        id="overview-header"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <DashboardIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Overview
          </Typography>

          {!expanded && metrics && !loading && !error && (
            <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Estimated completion time">
                <Chip
                  icon={<AccessTimeIcon style={{ fontSize: '16px' }} />}
                  size="small"
                  label={`${metrics.estimated_completion_time} min`}
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
              <Tooltip title="Total questions">
                <Chip
                  icon={<QuizIcon style={{ fontSize: '16px' }} />}
                  size="small"
                  label={`${metrics.total_questions} Questions`}
                  color="default"
                  variant="outlined"
                />
              </Tooltip>
              {metrics.number_short_answer_questions > 0 && (
                <Tooltip title="Short answer questions">
                  <Chip
                    icon={<ShortTextIcon style={{ fontSize: '16px' }} />}
                    size="small"
                    label={metrics.number_short_answer_questions}
                    color="default"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {metrics.number_paragraph_questions > 0 && (
                <Tooltip title="Long answer questions">
                  <Chip
                    icon={<NotesIcon style={{ fontSize: '16px' }} />}
                    size="small"
                    label={metrics.number_paragraph_questions}
                    color="default"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>
          )}

          {!expanded && loading && (
            <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={100} height={24} />
              <Skeleton variant="rounded" width={120} height={24} />
            </Box>
          )}

          {!expanded && error && (
            <Typography color="error" variant="caption" sx={{ ml: 'auto' }}>
              Unable to load metrics
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, pb: 2, backgroundColor: 'background.paper' }}>
        <Box sx={{ bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
          {/* Top section with time estimate and statistics in a row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            {/* Left column - Time estimate with clock visual */}
            <Box sx={{ mb: { xs: 2, sm: 0 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '180px' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Estimated Completion Time</Typography>
              {loading ? (
                <Skeleton variant="rectangular" width={160} height={70} sx={{ borderRadius: 2 }} />
              ) : error ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AccessTimeIcon color="disabled" sx={{ fontSize: 40, mr: 1, opacity: 0.7 }} />
                  <Typography variant="subtitle1" fontWeight="medium">--</Typography>
                </Box>
              ) : metrics ? (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  p: 1.5,
                  mt: 0.5,
                  width: 'fit-content'
                }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    mr: 2
                  }}>
                    <AccessTimeIcon fontSize="medium" />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" fontWeight="medium">{metrics.estimated_completion_time}</Typography>
                    <Typography variant="caption" sx={{ lineHeight: 1 }}>minutes</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AccessTimeIcon color="disabled" sx={{ fontSize: 40, mr: 1, opacity: 0.7 }} />
                  <Typography variant="subtitle1" fontWeight="medium">--</Typography>
                </Box>
              )}
            </Box>

            {/* Right column - Statistics */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Application Statistics</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} width={i % 2 === 0 ? 100 : 130} height={24} />
                  ))}
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ py: 0, fontSize: '0.875rem' }}>{error}</Alert>
              ) : metrics ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Total questions - always shown prominently */}
                  <Typography variant="body1" fontWeight="medium">
                    <strong>{metrics.total_questions}</strong> Total Questions
                  </Typography>

                  {/* Question types in a grid */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    columnGap: 3,
                    rowGap: 1
                  }}>
                    <Typography variant="body2">
                      <strong>{metrics.number_short_answer_questions}</strong> Short Answer
                    </Typography>
                    <Typography variant="body2">
                      <strong>{metrics.number_paragraph_questions}</strong> Paragraph
                    </Typography>
                    <Typography variant="body2">
                      <strong>{metrics.number_multiple_choice_questions}</strong> Multiple Choice
                    </Typography>
                    <Typography variant="body2">
                      <strong>{metrics.number_dropdown_questions}</strong> Dropdown
                    </Typography>
                    <Typography variant="body2">
                      <strong>{metrics.number_checkbox_questions}</strong> Checkbox
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2">No application questions configured</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ApplicationOverviewPanel;
